import { useState } from "react";
import { useParams } from "react-router";
import { useQueryClient } from "@tanstack/react-query";
import { Badge, Button, Dialog, EmptyState, Field, Input, Segmented, useToast, type Tone } from "@agentsaas/ui";
import { Loading } from "../layout/Loading";
import type { Credential, CredentialKind } from "@agentsaas/protocol";
import { useErrorText, useT } from "../app/i18n";
import { formatNumber, formatTime } from "../app/format";
import { useServices } from "../app/services";
import { useUser } from "../app/session";
import { qk, useCredentials } from "../data/queries";
import { ShellSlot } from "../layout/shell";
import { TenantSwitcher } from "./ProjectsPage";

const STATUS_TONE: Record<Credential["status"], Tone> = { unverified: "warning", valid: "success", invalid: "danger", revoked: "neutral" };

export function CredentialsPage() {
  const { tid = "" } = useParams();
  const { t } = useT();
  const errText = useErrorText();
  const { api } = useServices();
  const qc = useQueryClient();
  const toast = useToast();
  const creds = useCredentials(tid);
  const [adding, setAdding] = useState(false);
  const [revoking, setRevoking] = useState<Credential | null>(null);
  const [editing, setEditing] = useState<Credential | null>(null);
  const [busy, setBusy] = useState<string | null>(null);
  const refresh = () => qc.invalidateQueries({ queryKey: qk.credentials(tid) });

  const verify = async (c: Credential) => {
    setBusy(c.id);
    try {
      await api.call("credentialVerify", { params: { cid: c.id } });
      toast.show({ message: t("credential.verified"), tone: "success" });
    } catch (err) {
      toast.show({ message: errText(err), tone: "danger" });
    } finally {
      setBusy(null);
      void refresh();
    }
  };
  const revoke = async (c: Credential) => {
    try {
      await api.call("credentialDelete", { params: { cid: c.id } });
      toast.show({ message: t("credential.revoked_done") });
      setRevoking(null);
    } catch (err) {
      toast.show({ message: errText(err), tone: "danger" });
    } finally {
      void refresh();
    }
  };

  return (
    <div className="page">
      <ShellSlot name="title">
        <TenantSwitcher tid={tid} />
      </ShellSlot>
      <div className="page__head">
        <h1 className="page__title">{t("credential.title")}</h1>
        <Button variant="primary" icon="plus" onClick={() => setAdding(true)} data-testid="add-credential">
          {t("credential.add")}
        </Button>
      </div>
      {creds.isPending ? (
        <Loading />
      ) : creds.data?.length ? (
        <ul className="list card" style={{ padding: 0 }}>
          {creds.data.map((c) => (
            <li key={c.id} className="list-item" data-testid="credential-row">
              <span className="grow">
                <strong>{c.label}</strong>
                <div className="muted small">
                  {t(`credential.kind.${c.kind}`)}
                  {c.hint ? ` · ${c.hint}` : ""}
                  {c.memberIds ? ` · ${c.memberIds.map((id) => creds.data?.find((x) => x.id === id)?.label ?? id).join("、")}` : ""}
                </div>
                <RuntimeLine c={c} />
              </span>
              <Badge tone={STATUS_TONE[c.status]}>{t(`credential.status.${c.status}`)}</Badge>
              {c.status !== "revoked" ? (
                <>
                  {c.kind === "pool" ? (
                    <Button size="sm" onClick={() => setEditing(c)} data-testid="pool-edit">
                      {t("credential.pool.edit")}
                    </Button>
                  ) : null}
                  <Button size="sm" loading={busy === c.id} onClick={() => void verify(c)}>
                    {t("credential.verify")}
                  </Button>
                  <Button size="sm" variant="ghost" onClick={() => setRevoking(c)}>
                    {t("credential.revoke")}
                  </Button>
                </>
              ) : null}
            </li>
          ))}
        </ul>
      ) : (
        <EmptyState icon="key" title={t("credential.empty")} />
      )}
      <AddCredentialDialog tid={tid} creds={creds.data ?? []} open={adding} onClose={() => setAdding(false)} onAdded={() => void refresh()} />
      {editing ? <EditPoolDialog pool={editing} creds={creds.data ?? []} onClose={() => setEditing(null)} onSaved={() => void refresh()} /> : null}
      <Dialog
        open={!!revoking}
        onClose={() => setRevoking(null)}
        title={t("credential.revoke")}
        closeLabel={t("shell.close")}
        size="sm"
        footer={
          <>
            <Button onClick={() => setRevoking(null)}>{t("common.cancel")}</Button>
            <Button variant="danger" onClick={() => revoking && void revoke(revoking)}>
              {t("credential.revoke")}
            </Button>
          </>
        }
      >
        <p>{revoking ? t("credential.revoke_confirm", { label: revoking.label }) : null}</p>
      </Dialog>
    </div>
  );
}

/** 订阅凭据的运行时状况：限流冷却、额度利用率、最近 5 小时用量 */
function RuntimeLine({ c }: { c: Credential }) {
  const { t, locale } = useT();
  const r = c.runtime;
  if (!r || c.status === "revoked") return null;
  const parts: string[] = [];
  if (r.utilization !== null) parts.push(t("credential.runtime.utilization", { percent: formatNumber(r.utilization, locale, { style: "percent", maximumFractionDigits: 0 }) }));
  parts.push(t("credential.runtime.tokens5h", { tokens: formatNumber(r.tokens5h, locale, { notation: "compact" }) }));
  return (
    <div className="small" data-testid="credential-runtime">
      {r.coolingUntil ? (
        <span className="form-error">{t("credential.runtime.cooling", { time: formatTime(r.coolingUntil, locale) })} · </span>
      ) : null}
      <span className="muted">{parts.join(" · ")}</span>
    </div>
  );
}

/** 可以放进池里的凭据：自己的、未吊销的订阅凭据 */
function poolCandidates(creds: Credential[], userId: string): Credential[] {
  return creds.filter((c) => c.kind === "subscription" && c.status !== "revoked" && c.ownerUserId === userId);
}

/** 勾选池成员；勾选顺序即优先顺序 */
function PoolMemberPicker({ creds, value, onChange }: { creds: Credential[]; value: string[]; onChange(ids: string[]): void }) {
  const { t } = useT();
  if (!creds.length) return <p className="muted small">{t("credential.pool.no_members")}</p>;
  return (
    <fieldset className="stack" style={{ gap: 6 }}>
      <legend className="ui-field__label">{t("credential.pool.members")}</legend>
      {creds.map((c) => {
        const idx = value.indexOf(c.id);
        return (
          <label key={c.id} className="check">
            <input type="checkbox" checked={idx >= 0} onChange={(e) => onChange(e.target.checked ? [...value, c.id] : value.filter((x) => x !== c.id))} data-testid="pool-member" />
            <span>
              {idx >= 0 ? `${idx + 1}. ` : ""}
              {c.label}
              <span className="muted small">
                {c.hint ? ` · ${c.hint}` : ""} · {t(`credential.status.${c.status}`)}
              </span>
            </span>
          </label>
        );
      })}
      <p className="muted small">{t("credential.pool.members_hint")}</p>
    </fieldset>
  );
}

function EditPoolDialog({ pool, creds, onClose, onSaved }: { pool: Credential; creds: Credential[]; onClose(): void; onSaved(): void }) {
  const { t } = useT();
  const errText = useErrorText();
  const { api } = useServices();
  const toast = useToast();
  const user = useUser();
  const [label, setLabel] = useState(pool.label);
  const [memberIds, setMemberIds] = useState<string[]>(pool.memberIds ?? []);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const save = async () => {
    setBusy(true);
    setError(null);
    try {
      await api.call("credentialUpdate", { params: { cid: pool.id }, body: { label, memberIds } });
      toast.show({ message: t("credential.pool.saved"), tone: "success" });
      onSaved();
      onClose();
    } catch (err) {
      setError(errText(err));
    } finally {
      setBusy(false);
    }
  };
  return (
    <Dialog
      open
      onClose={onClose}
      title={t("credential.pool.edit")}
      closeLabel={t("shell.close")}
      footer={
        <>
          <Button onClick={onClose}>{t("common.cancel")}</Button>
          <Button variant="primary" loading={busy} disabled={!label.trim() || !memberIds.length} onClick={() => void save()} data-testid="pool-save">
            {t("common.save")}
          </Button>
        </>
      }
    >
      <Field label={t("credential.label")}>{(id) => <Input id={id} value={label} onChange={(e) => setLabel(e.target.value)} maxLength={64} />}</Field>
      <PoolMemberPicker creds={poolCandidates(creds, pool.ownerUserId ?? user.id)} value={memberIds} onChange={setMemberIds} />
      {error ? (
        <p className="form-error" role="alert">
          {error}
        </p>
      ) : null}
    </Dialog>
  );
}

/** 不需要翻译的固定文本：命令与 token 格式 */
const SETUP_TOKEN_CMD = "claude setup-token";
const SUB_TOKEN_PLACEHOLDER = "sk-ant-oat01-…";

function AddCredentialDialog({ tid, creds, open, onClose, onAdded }: { tid: string; creds: Credential[]; open: boolean; onClose(): void; onAdded(): void }) {
  const { t } = useT();
  const errText = useErrorText();
  const { api } = useServices();
  const toast = useToast();
  const user = useUser();
  const [kind, setKindRaw] = useState<CredentialKind>("subscription");
  const [label, setLabel] = useState(() => t("credential.sub.default_label"));
  const [token, setToken] = useState("");
  const [memberIds, setMemberIds] = useState<string[]>([]);
  const setKind = (k: CredentialKind) => {
    setKindRaw(k);
    setLabel(k === "pool" ? t("credential.pool.default_label") : k === "subscription" ? t("credential.sub.default_label") : "");
  };
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const submit = async () => {
    setBusy(true);
    setError(null);
    try {
      const body = kind === "subscription" ? { kind, label, token: token.replace(/\s+/g, "") } : kind === "pool" ? { kind, label, memberIds } : { kind, label };
      const created = await api.call("credentialCreate", { params: { tid }, body });
      // 订阅 token 创建后立即校验一次，让用户马上知道能不能用
      if (kind === "subscription") {
        const v = await api.call("credentialVerify", { params: { cid: created.id } }).catch((e: unknown) => {
          toast.show({ message: `${t("credential.sub.verify_failed")}：${errText(e)}`, tone: "danger" });
          return null;
        });
        if (v?.status === "valid") toast.show({ message: t("credential.verified"), tone: "success" });
      }
      setKind("subscription");
      setToken("");
      setMemberIds([]);
      onAdded();
      onClose();
    } catch (err) {
      setError(errText(err));
    } finally {
      setBusy(false);
    }
  };
  return (
    <Dialog
      open={open}
      onClose={onClose}
      title={t("credential.add")}
      closeLabel={t("shell.close")}
      footer={
        <>
          <Button onClick={onClose}>{t("common.cancel")}</Button>
          <Button variant="primary" loading={busy} disabled={!label.trim() || (kind === "subscription" && token.length < 10) || (kind === "pool" && !memberIds.length)} onClick={() => void submit()}>
            {t("common.create")}
          </Button>
        </>
      }
    >
      <div className="ui-field">
        <span className="ui-field__label">{t("credential.kind")}</span>
        <Segmented<CredentialKind>
          label={t("credential.kind")}
          value={kind}
          onChange={setKind}
          options={[
            { value: "subscription", label: t("credential.kind.subscription") },
            { value: "pool", label: t("credential.kind.pool") },
            { value: "platform", label: t("credential.kind.platform") },
          ]}
        />
      </div>
      <Field label={t("credential.label")}>{(id) => <Input id={id} value={label} onChange={(e) => setLabel(e.target.value)} maxLength={64} />}</Field>
      {kind === "subscription" ? (
        <>
          <div className="sub-howto" data-testid="sub-howto">
            <strong className="small">{t("credential.sub.howto_title")}</strong>
            <ol className="small">
              <li>{t("credential.sub.step1")}</li>
              <li>
                {t("credential.sub.step2")}
                <span className="sub-howto__cmd">
                  <code>{SETUP_TOKEN_CMD}</code>
                  <Button
                    size="sm"
                    onClick={() => {
                      void navigator.clipboard?.writeText(SETUP_TOKEN_CMD).then(() => toast.show({ message: t("credential.sub.copied") }));
                    }}
                  >
                    {t("credential.sub.copy")}
                  </Button>
                </span>
              </li>
              <li>{t("credential.sub.step3")}</li>
            </ol>
          </div>
          <Field label={t("credential.token")} hint={t("credential.token_hint")} error={error ?? undefined}>
            {(id, d) => (
              <Input id={id} type="password" autoComplete="off" placeholder={SUB_TOKEN_PLACEHOLDER} aria-describedby={d} value={token} onChange={(e) => setToken(e.target.value)} data-testid="sub-token" />
            )}
          </Field>
          {/\S\s+\S/.test(token.trim()) ? (
            <p className="muted small" data-testid="sub-token-warn">{t("credential.sub.whitespace_note")}</p>
          ) : null}
          {token.trim().startsWith("sk-ant-api") ? (
            <p className="form-error small" role="alert" data-testid="sub-token-warn">{t("credential.sub.api_key_warn")}</p>
          ) : token.trim().length >= 10 && !token.trim().startsWith("sk-ant-oat") ? (
            <p className="muted small" data-testid="sub-token-warn">{t("credential.sub.format_warn")}</p>
          ) : null}
          <p className="muted small">{t("credential.subscription_note")}</p>
        </>
      ) : (
        <>
          {kind === "pool" ? (
            <>
              <p className="muted small">{t("credential.pool.note")}</p>
              <PoolMemberPicker creds={poolCandidates(creds, user.id)} value={memberIds} onChange={setMemberIds} />
            </>
          ) : null}
          {error ? (
            <p className="form-error" role="alert">
              {error}
            </p>
          ) : null}
        </>
      )}
    </Dialog>
  );
}
